import { supabase } from "@/lib/supabase";

export type UserRole = "owner" | "renter" | "admin";

/**
 * Normalizes database role to code role
 * Maps: host, proprietaire → owner
 *       locataire, renter → renter
 *       admin → admin
 */
export function normalizeRole(dbRole: string | null | undefined): UserRole | null {
  if (!dbRole) return null;

  const normalized = dbRole.toLowerCase().trim();

  if (normalized === "host" || normalized === "proprietaire" || normalized === "owner") {
    return "owner";
  }
  if (normalized === "locataire" || normalized === "renter") {
    return "renter";
  }
  if (normalized === "admin") {
    return "admin";
  }

  return null;
}

/**
 * Maps code role to database role
 */
export function mapCodeRoleToDbRole(role: UserRole): string {
  const mapping: Record<UserRole, string> = {
    owner: "proprietaire",
    renter: "locataire",
    admin: "admin",
  };
  return mapping[role];
}

/**
 * Cached role service with in-memory cache
 */
class RoleService {
  private roleCache = new Map<string, { role: UserRole | null; timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  async getUserRole(userId: string, forceRefresh = false): Promise<UserRole | null> {
    // Check cache first
    if (!forceRefresh) {
      const cached = this.roleCache.get(userId);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return cached.role;
      }
    }

    try {
      // Single optimized query - only fetch role field
      const { data, error } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", userId)
        .single();

      if (import.meta.env.DEV) {
        console.log(`[RoleService] Fetched for ${userId}:`, { data, error });
      }

      if (error) {
        // If profile doesn't exist, try to create it
        if (error.code === "PGRST116") {
          try {
            const { data: createdProfiles, error: createError } =
              await supabase.rpc("get_or_create_profile");
            if (!createError && createdProfiles && createdProfiles.length > 0) {
              // Retry immediately after profile creation (no artificial delay)
              const { data: retryData, error: retryError } = await supabase
                .from("profiles")
                .select("role")
                .eq("id", userId)
                .single();

              if (!retryError && retryData) {
                const normalizedRole = normalizeRole(retryData.role);
                this.roleCache.set(userId, {
                  role: normalizedRole,
                  timestamp: Date.now(),
                });
                return normalizedRole;
              }
            }
          } catch (rpcErr) {
            console.warn("Error creating profile:", rpcErr);
          }
        }

        console.error("Error fetching role:", error);
        return null;
      }

      const normalizedRole = normalizeRole(data?.role);

      if (normalizedRole) {
        this.roleCache.set(userId, {
          role: normalizedRole,
          timestamp: Date.now(),
        });
      }

      return normalizedRole;
    } catch (error) {
      console.error("Exception in getUserRole:", error);
      return null;
    }
  }

  invalidateCache(userId?: string) {
    if (userId) {
      this.roleCache.delete(userId);
    } else {
      this.roleCache.clear();
    }
  }
}

export const roleService = new RoleService();

/**
 * Resolves the authenticated user's role from the profile first (source of truth),
 * then auth metadata. Always takes the Auth user object so callers are not blocked
 * by stale React state. Bypasses the in-memory cache so a Supabase role change
 * is picked up on the next login or refresh.
 */
export async function resolveAuthRole(
  user: { id: string; user_metadata?: Record<string, unknown> } | null
): Promise<UserRole | null> {
  if (!user) return null;

  const profileRole = await roleService.getUserRole(user.id, true);
  if (profileRole) return profileRole;

  return normalizeRole(
    typeof user.user_metadata?.role === "string" ? user.user_metadata.role : null
  );
}

/**
 * Sets renter/owner only when the profile role is still NULL.
 * Used by complete-profile; cannot escalate to admin or change an existing role.
 */
export async function assignInitialProfileRole(
  userId: string,
  role: "renter" | "owner"
): Promise<UserRole | null> {
  const { data, error } = await supabase.rpc("assign_initial_profile_role", {
    p_role: role,
  });

  if (error) {
    if (import.meta.env.DEV) {
      console.error("[RoleService] assign_initial_profile_role failed:", error);
    }
    return null;
  }

  const normalized = normalizeRole(data);
  roleService.invalidateCache(userId);
  return normalized;
}
